import {
  CurrencyHelper,
  CurrencyPattern,
  NumberConverter,
  NumberConverterOption,
} from '@marketplace/myconverter';

import Decimal from 'decimal.js';
import { DEFAULT_CURRENCY } from '..';

declare const i18next: any;

export const parseNumber = ({
  number,
  pattern,
  option,
}: {
  number: number | bigint;
  pattern?: keyof typeof CurrencyPattern;
  option?: Partial<NumberConverterOption>;
}) => {
  function convertToEnumAuto(
    value: keyof typeof CurrencyPattern,
  ): CurrencyPattern {
    return CurrencyPattern?.[value] || CurrencyPattern.BetVisualFull;
  }

  return CurrencyHelper.doFormat(
    Number(number),
    convertToEnumAuto(pattern),
    option,
  );
};

export const parseCurrency = ({
  value,
  rootCurrency,
  rates,
  isRealMoney,
}: {
  value: number;
  rootCurrency: string;
  rates: Record<string, number>;
  isRealMoney?: boolean;
}) => {
  if (!rates) {
    return '';
  }
  return parseNumber({
    number: convertedBalance({
      value: value,
      dataRates: rates || {},
      rootCurrency: rootCurrency,
      isRealMoney,
    }),
    option: {
      currency: getCurrentCurrency(),
      currencyDisplay: 'none',
      symbolPosition: 'after',
      ratio: 1000,
      displayFractionDigitsDefault: true,
      maximumFractionDigits: 2,
      space: true,
      hideFractionDigits: ['vno', 'ido']?.includes(getCurrentCurrency()),
    },
  });
};

export const CURRENCY_RATIO_DEFAULT = 1000;

export const divideByRatio = (value?: number) => {
  return NumberConverter.divideByRatio(value, CURRENCY_RATIO_DEFAULT);
};

export const multiplyByRatio = (value?: number) => {
  return NumberConverter.multiplyByRatio(value, CURRENCY_RATIO_DEFAULT);
};

export enum Currency {
  ALL = 'all',
  USD = 'usd',
  VND = 'vnd',
  VNO = 'vno',
  THB = 'thb',
  MYR = 'myr',
  CNY = 'cny',
  TWD = 'twd',
  HKD = 'hkd',
  IDR = 'idr',
  XTR = 'xtr',
  TON = 'ton',
  PHP = 'php',
  IDO = 'ido',
  SGD = 'sgd',
  NCC = 'ncc',
}

export const MapRealCurrency: Record<string, number> = {
  vnd: 1000,
  idr: 1000,
};

const CurrencyOptions = [
  {
    value: Currency.USD,
    name: 'USD',
  },
  {
    value: Currency.VND,
    name: 'VND',
  },
  {
    value: Currency.VNO,
    name: 'VNO',
  },
  {
    value: Currency.THB,
    name: 'THB',
  },
  {
    value: Currency.MYR,
    name: 'MYR',
  },
  {
    value: Currency.CNY,
    name: 'CNY',
  },
  {
    value: Currency.TWD,
    name: 'TWD',
  },
  {
    value: Currency.HKD,
    name: 'HKD',
  },
  {
    value: Currency.IDR,
    name: 'IDR',
  },
  {
    value: Currency.IDO,
    name: 'IDO',
  },
  {
    value: Currency.XTR,
    name: 'XTR',
  },
  {
    value: Currency.TON,
    name: 'TON',
  },
  {
    value: Currency.PHP,
    name: 'PHP',
  },
  {
    value: Currency.SGD,
    name: 'SGD',
  },
  {
    value: Currency.NCC,
    name: 'NCC',
  },
];

export const getCurrencyOptions = (isAll?: boolean) => {
  if (!isAll) return CurrencyOptions;
  return [
    {
      value: Currency.ALL,
      name: i18next.t('ALL'),
    },
    ...CurrencyOptions,
  ];
};

const SHOW_IN_KEY = 'SHOW_IN';

export const getCurrencyFromLocalStorage = () => {
  return localStorage.getItem(SHOW_IN_KEY);
};

export const getCurrencyDefault = () => {
  return getCurrencyOptions()[0].value;
};

export const setCurrencyToLocalStorage = (currency: string) => {
  localStorage.setItem(SHOW_IN_KEY, currency);
};

export const getCurrentCurrency = () =>
  getCurrencyFromLocalStorage() || getCurrencyDefault();

export type ConvertedBalance = {
  value: number;
  dataRates: Record<string, number>;
  rootCurrency?: string;
  multiRatio?: boolean;
  isRealMoney?: boolean;
  currency?: string;
};

export const convertedBalance = ({
  value,
  multiRatio,
  dataRates,
  rootCurrency = '',
  isRealMoney,
  currency,
}: ConvertedBalance) => {
  const showIn = currency || getCurrentCurrency().toString();

  if (multiRatio) {
    value = Number(multiplyByRatio(value));
  }

  if (isRealMoney) {
    value /= MapRealCurrency[rootCurrency.toLowerCase()] || 1;
  }

  const rootValue = new Decimal(value);

  const rate = new Decimal((Number(dataRates?.[showIn] || 1) /
  Number(dataRates?.[rootCurrency.toLowerCase()] || 1)))

  let convert =  Number(
    rootValue.times(rate)
  );

  return convert
};

export const settingNumberConverter = (currency?: string) => {
  if (!currency) {
    currency = getCurrentCurrency().toString();
  }

  CurrencyHelper.setting({
    currency: currency || DEFAULT_CURRENCY,
    ratio: CURRENCY_RATIO_DEFAULT,
  });
};
